/**
 * Device chat threads use socket uuid (`uuid_device`). Map filters and REST stores use device primary key `id`.
 */

const STORE_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * True when `id` is a REST primary key (UUID), not a socket uuid / operator / system label.
 * GET /device/:id and GET /tracker/:id only accept this shape (ASP.NET Guid → 400 otherwise).
 * @param {unknown} id
 * @returns {boolean}
 */
export function isDeviceRestStoreId (id) {
  return id != null && id !== '' && STORE_ID_PATTERN.test(String(id))
}

/**
 * Legacy wire labels for platform-originated chat rows (`uuid_device_sender` = operator | system).
 * @param {unknown} id
 * @returns {boolean}
 */
export function isPlatformChatDeviceId (id) {
  if (id == null || id === '') return false
  const normalized = String(id).toLowerCase()
  return normalized === 'operator' || normalized === 'system'
}

/**
 * Socket / legacy DB uuid for a device row (phones: uuidDevice, GPS trackers: uuid).
 * @param {{ uuidDevice?: string, uuid?: string } | null | undefined} device
 * @returns {string | null}
 */
export function getDeviceSocketUuid (device) {
  const value = device?.uuidDevice || device?.uuid
  return value ? String(value) : null
}

/**
 * @param {Record<string, { id?: string, uuidDevice?: string, uuid?: string }>} devicesById
 * @param {string | number | null | undefined} deviceIdOrUuid
 * @returns {{ storeId: string | null, uuid: string | null }}
 */
export function resolveDeviceChatIds (devicesById, deviceIdOrUuid) {
  if (deviceIdOrUuid == null || deviceIdOrUuid === '' || isPlatformChatDeviceId(deviceIdOrUuid)) {
    return { storeId: null, uuid: null }
  }

  const needle = String(deviceIdOrUuid)
  const direct = devicesById?.[needle]
  if (direct) {
    return { storeId: needle, uuid: getDeviceSocketUuid(direct) }
  }

  const match = Object.entries(devicesById || {}).find(
    ([, device]) => getDeviceSocketUuid(device) === needle
  )
  if (match) {
    return { storeId: match[0], uuid: needle }
  }

  return { storeId: null, uuid: needle }
}

/**
 * Socket uuid for an open device thread. Returns null while a GPS tracker REST id is still
 * hydrating (avoid matching messages with the store GUID).
 *
 * @param {{ devicesById?: Record<string, object>, deviceId?: string | null, device?: object | null }} params
 * @returns {string | null}
 */
export function resolveThreadDeviceUuid ({ devicesById, deviceId, device }) {
  const fromDevice = getDeviceSocketUuid(device)
  if (fromDevice) return fromDevice

  if (!deviceId || isPlatformChatDeviceId(deviceId)) return null

  const { storeId, uuid } = resolveDeviceChatIds(devicesById, deviceId)
  if (storeId) return uuid

  if (/^\d+$/.test(String(deviceId))) {
    return String(deviceId)
  }

  if (isDeviceRestStoreId(deviceId)) {
    return null
  }

  return uuid || null
}
